import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BookOpen, Check, Copy, KeyRound, Pause, Play, Plus, RefreshCw, RotateCcw, Send, Trash2, Webhook } from "lucide-react";
import { useState, type ReactNode } from "react";
import { toast } from "sonner";
import { PageHeader, useTenant } from "@/components/AppShell";
import { areaLevelSummary, choiceLabel, PermissionsPicker, type Choice } from "@/components/PermissionsEditor";
import { Badge, Button, Card, cx, EmptyState, Input, Skeleton } from "@/components/ui";
import { api, ApiError, canManage, effectivePermissions, PERMISSION_PRESETS, WEBHOOK_EVENTS, WEBHOOK_EVENT_KEYS, type Permissions, type WebhookItem } from "@/lib/api";
import { fmtRelative } from "@/lib/format";

import { t } from "@/lib/i18n";
const errMsg = (e: unknown, f = "Aktion fehlgeschlagen") => (e instanceof ApiError ? ((e.body as { message?: string })?.message ?? f) : f);
const copy = (text: string, what = "Kopiert") => void navigator.clipboard.writeText(text).then(() => toast.success(what));

function DialogShell({ open, onClose, title, description, children, wide }: { open: boolean; onClose: () => void; title: string; description?: string; children: ReactNode; wide?: boolean }) {
  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30 animate-fade-in" />
        <Dialog.Content className={cx("fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 max-h-[90vh] overflow-y-auto rounded-2xl border border-border bg-surface p-6 shadow-2xl animate-pop-in", wide ? "w-[min(720px,calc(100vw-32px))]" : "w-[min(560px,calc(100vw-32px))]")}>
          <Dialog.Title className="text-[17px] font-semibold">{t(title)}</Dialog.Title>
          {description && <Dialog.Description className="text-fg-muted mt-1 text-[13px]">{t(description)}</Dialog.Description>}
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Geheimnis genau einmal zeigen – mit Kopieren und Bestätigung */
function SecretOnce({ label, value, hint, onDone }: { label: string; value: string; hint: string; onDone: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="mt-4 space-y-3">
      <div className="rounded-xl border border-warning/30 bg-warning-soft/60 px-4 py-3 text-[12.5px]">{t(hint)}</div>
      <p className="text-[12px] font-medium text-fg-muted">{t(label)}</p>
      <div className="flex gap-2">
        <code className="flex-1 min-w-0 rounded-lg border border-border bg-surface-2 px-3 py-2 font-mono text-[13px] break-all select-all">{value}</code>
        <Button icon={copied ? <Check className="size-4" /> : <Copy className="size-4" />} onClick={() => { copy(value); setCopied(true); }}>{t("Kopieren")}</Button>
      </div>
      <div className="flex justify-end"><Button variant="primary" disabled={!copied} onClick={onDone} title={copied ? undefined : t("Bitte zuerst kopieren")}>{t("Gesichert – fertig")}</Button></div>
    </div>
  );
}

function ApiKeysCard() {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["api-keys", tenantId], queryFn: () => api.integrations.keys(tenantId) });
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [choice, setChoice] = useState<Choice>("readonly");
  const [custom, setCustom] = useState<Permissions>(effectivePermissions("customer_user", { areas: PERMISSION_PRESETS.readonly.permissions }));
  const [created, setCreated] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () => {
      const areas = choice === "custom" || choice === "admin" ? custom : PERMISSION_PRESETS[choice].permissions;
      return api.integrations.createKey(tenantId, name.trim(), { preset: choice, areas: { ...areas, settings: "none" } });
    },
    onSuccess: (r) => { setCreated(r.key); void qc.invalidateQueries({ queryKey: ["api-keys", tenantId] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const revoke = useMutation({ mutationFn: (id: string) => api.integrations.revokeKey(tenantId, id), onSuccess: () => { toast.success("Widerrufen – der Schlüssel funktioniert ab sofort nicht mehr"); void qc.invalidateQueries({ queryKey: ["api-keys", tenantId] }); } });
  const close = () => { setOpen(false); setCreated(null); setName(""); setChoice("readonly"); };
  const active = (q.data ?? []).filter((k) => !k.revokedAt);
  const revoked = (q.data ?? []).filter((k) => k.revokedAt);
  return (
    <Card className="overflow-hidden">
      <div className="p-5 flex flex-wrap items-start gap-3">
        <span className="size-9 rounded-xl grid place-items-center bg-surface-2 text-fg-muted"><KeyRound className="size-4" /></span>
        <div className="min-w-0 flex-1">
          <h2 className="font-semibold">{t("API-Schlüssel")}</h2>
          <p className="text-[12.5px] text-fg-muted mt-0.5">{t("Für eure Warenwirtschaft oder euer ERP. Jeder Schlüssel hat eigene Rechte (wie eine Rolle) und lässt sich jederzeit widerrufen. Einstellungen sind per Schlüssel nie änderbar.")}</p>
        </div>
        <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setOpen(true)}>{t("Schlüssel erstellen")}</Button>
      </div>
      <ul className="divide-y divide-border border-t border-border">
        {q.isLoading && <li className="px-5 py-3"><Skeleton className="h-4 w-1/2" /></li>}
        {q.data && !active.length && <li className="px-5 py-6 text-center text-[13px] text-fg-muted">{t("Noch kein Schlüssel.")}</li>}
        {active.map((k) => (
          <li key={k.id} className="px-5 py-3 flex flex-wrap items-center gap-3 text-[13px]">
            <span className="min-w-0 flex-1">
              <span className="font-medium">{k.name}</span> <span className="font-mono text-[12px] text-fg-subtle">{k.prefix}…</span>
              <span className="block text-[11.5px] text-fg-subtle truncate">{choiceLabel(k.preset)} · {areaLevelSummary(k.permissions) || t("keine Bereiche")}</span>
            </span>
            <span className="text-[12px] text-fg-muted">{k.lastUsedAt ? t("zuletzt benutzt {v}", { v: fmtRelative(k.lastUsedAt) }) : t("noch nie benutzt")}</span>
            <Button size="sm" variant="ghost" icon={<Trash2 className="size-3.5" />} onClick={() => confirm(`Schlüssel „${k.name}“ widerrufen? Systeme, die ihn nutzen, verlieren sofort den Zugriff.`) && revoke.mutate(k.id)}>{t("Widerrufen")}</Button>
          </li>
        ))}
        {revoked.length > 0 && <li className="px-5 py-2 text-[12px] text-fg-subtle">{revoked.length} {t("widerrufene")} {revoked.length === 1 ? t("Schlüssel") : t("Schlüssel")}: {revoked.map((k) => k.name).join(", ")}</li>}
      </ul>
      <DialogShell open={open} onClose={close} title={created ? t("Schlüssel erstellt") : t("API-Schlüssel erstellen")} description={created ? undefined : t("Rechte so knapp wie möglich wählen – z. B. „Nur lesen“ für Auswertungen.")} wide>
        {created ? (
          <SecretOnce label={t("API-Schlüssel")} value={created} hint={t("Der Schlüssel wird nur jetzt angezeigt. Bitte sicher im System hinterlegen (nicht per E-Mail versenden). Bei Verlust einfach widerrufen und neu erstellen.")} onDone={close} />
        ) : (
          <>
            <label className="block mt-4">
              <span className="text-[12px] font-medium text-fg-muted">{t("Name")}</span>
              <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder={t("z. B. JTL-Wawi Abgleich")} className="mt-1" />
            </label>
            <div className="mt-4"><PermissionsPicker choice={choice} onChoice={setChoice} custom={custom} onCustom={setCustom} allowAdmin={false} hideAreas={["settings"]} /></div>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="ghost" onClick={close}>{t("Abbrechen")}</Button>
              <Button variant="primary" loading={create.isPending} disabled={!name.trim()} onClick={() => create.mutate()}>{t("Erstellen")}</Button>
            </div>
          </>
        )}
      </DialogShell>
    </Card>
  );
}

function WebhookRow({ w }: { w: WebhookItem }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const refresh = () => void qc.invalidateQueries({ queryKey: ["webhooks", tenantId] });
  const [showLog, setShowLog] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);
  const test = useMutation({ mutationFn: () => api.integrations.testWebhook(tenantId, w.id), onSuccess: (r) => (r.ok ? toast.success(`Test angekommen (HTTP ${r.status})`) : toast.error(`Test fehlgeschlagen: ${r.error ?? `HTTP ${r.status}`}`)) });
  const toggle = useMutation({ mutationFn: () => api.integrations.updateWebhook(tenantId, w.id, { active: !w.active }), onSuccess: () => { toast.success(w.active ? "Pausiert" : "Wieder aktiv – ausstehende Zustellungen werden gesendet"); refresh(); } });
  const del = useMutation({ mutationFn: () => api.integrations.deleteWebhook(tenantId, w.id), onSuccess: () => { toast.success("Gelöscht"); refresh(); } });
  const rotate = useMutation({ mutationFn: () => api.integrations.rotateSecret(tenantId, w.id), onSuccess: (r) => setSecret(r.secret) });
  const log = useQuery({ queryKey: ["webhook-log", tenantId, w.id], queryFn: () => api.integrations.deliveries(tenantId, w.id), enabled: showLog });
  const redeliver = useMutation({ mutationFn: (ids: string[]) => api.integrations.redeliver(tenantId, ids), onSuccess: (r) => { toast.success(`${r.queued} erneut eingeplant`); void log.refetch(); refresh(); } });
  return (
    <li className="px-5 py-3">
      <div className="flex flex-wrap items-center gap-3 text-[13px]">
        <span className="min-w-0 flex-1">
          <span className="font-mono text-[12.5px] break-all">{w.url}</span>
          {w.description && <span className="text-fg-muted"> · {t(w.description)}</span>}
          <span className="block text-[11.5px] text-fg-subtle">{w.events.map((e) => WEBHOOK_EVENTS[e as keyof typeof WEBHOOK_EVENTS] ?? e).join(" · ")}</span>
          <span className="block text-[11.5px] text-fg-subtle">
            {w.delivered24h} {t("in 24 h zugestellt")}{w.pending ? t(" · {pending} ausstehend", { pending: w.pending }) : ""}{w.failed ? t(" · {failed} aufgegeben", { failed: w.failed }) : ""}{w.lastSuccessAt ? t(" · zuletzt {v}", { v: fmtRelative(w.lastSuccessAt) }) : ""}
          </span>
        </span>
        {w.active ? (w.consecutiveFailures ? <Badge tone="warning">{w.consecutiveFailures} {t("Fehler in Folge")}</Badge> : <Badge tone="success">{t("aktiv")}</Badge>) : <Badge tone="danger">{t("pausiert")}</Badge>}
        <div className="flex flex-wrap gap-1">
          <Button size="sm" variant="ghost" icon={<Send className="size-3.5" />} loading={test.isPending} onClick={() => test.mutate()}>{t("Test")}</Button>
          <Button size="sm" variant="ghost" onClick={() => setShowLog((s) => !s)}>{t("Zustellungen")}</Button>
          <Button size="sm" variant="ghost" icon={w.active ? <Pause className="size-3.5" /> : <Play className="size-3.5" />} onClick={() => toggle.mutate()}>{w.active ? t("Pausieren") : t("Aktivieren")}</Button>
          <Button size="sm" variant="ghost" icon={<RefreshCw className="size-3.5" />} onClick={() => confirm("Neues Geheimnis erzeugen? Das bisherige gilt sofort nicht mehr – Empfänger muss umgestellt werden.") && rotate.mutate()}>{t("Geheimnis")}</Button>
          <Button size="sm" variant="ghost" icon={<Trash2 className="size-3.5" />} onClick={() => confirm(`Webhook ${w.url} löschen?`) && del.mutate()} aria-label={t("Löschen")} />
        </div>
      </div>
      {w.disabledReason && <p className="mt-2 text-[12px] text-danger">{w.disabledReason}</p>}
      {showLog && (
        <div className="mt-3 rounded-xl border border-border overflow-hidden">
          {log.isLoading && <div className="p-3"><Skeleton className="h-4 w-1/2" /></div>}
          {log.data?.length === 0 && <p className="p-3 text-[12.5px] text-fg-muted">{t("Noch keine Zustellungen.")}</p>}
          <ul className="divide-y divide-border text-[12.5px]">
            {log.data?.map((d) => (
              <li key={d.id} className="px-3 py-2 flex flex-wrap items-center gap-2">
                <span className="font-mono">{d.event}</span>
                <span className="text-fg-subtle">{fmtRelative(d.createdAt)}</span>
                <span className="ml-auto">
                  {d.status === "success" ? <Badge tone="success">{t("zugestellt")}{d.responseStatus ? ` · ${d.responseStatus}` : ""}</Badge> : d.status === "pending" ? <Badge tone="warning">{d.attempts ? t("Versuch {attempts} · {v} · nächster {v2}", { attempts: d.attempts, v: d.error ?? "", v2: fmtRelative(d.nextAttemptAt) }) : t("ausstehend")}</Badge> : <Badge tone="danger">{t("aufgegeben ·")} {d.error}</Badge>}
                </span>
                {d.status !== "success" && <Button size="sm" variant="ghost" icon={<RotateCcw className="size-3" />} onClick={() => redeliver.mutate([d.id])}>{t("Jetzt senden")}</Button>}
              </li>
            ))}
          </ul>
        </div>
      )}
      <DialogShell open={!!secret} onClose={() => setSecret(null)} title={t("Neues Signatur-Geheimnis")}>
        {secret && <SecretOnce label={t("Geheimnis")} value={secret} hint={t("Nur jetzt sichtbar. Im Empfänger hinterlegen – Zustellungen werden ab sofort damit signiert.")} onDone={() => setSecret(null)} />}
      </DialogShell>
    </li>
  );
}

function WebhooksCard() {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["webhooks", tenantId], queryFn: () => api.integrations.webhooks(tenantId), refetchInterval: 30_000 });
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [events, setEvents] = useState<string[]>(["order.shipped"]);
  const [secret, setSecret] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () => api.integrations.createWebhook(tenantId, { url: url.trim(), ...(description.trim() ? { description: description.trim() } : {}), events }),
    onSuccess: (r) => { setSecret(r.secret); void qc.invalidateQueries({ queryKey: ["webhooks", tenantId] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const close = () => { setOpen(false); setSecret(null); setUrl(""); setDescription(""); setEvents(["order.shipped"]); };
  return (
    <Card className="overflow-hidden">
      <div className="p-5 flex flex-wrap items-start gap-3">
        <span className="size-9 rounded-xl grid place-items-center bg-surface-2 text-fg-muted"><Webhook className="size-4" /></span>
        <div className="min-w-0 flex-1">
          <h2 className="font-semibold">{t("Webhooks")}</h2>
          <p className="text-[12.5px] text-fg-muted mt-0.5">{t("Logiful meldet Ereignisse sofort an euer System – signiert, mit automatischer Wiederholung bis zu 24 Stunden.")}</p>
        </div>
        <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setOpen(true)}>{t("Webhook hinzufügen")}</Button>
      </div>
      <ul className="divide-y divide-border border-t border-border">
        {q.isLoading && <li className="px-5 py-3"><Skeleton className="h-4 w-1/2" /></li>}
        {q.data?.length === 0 && <li className="px-5 py-6 text-center text-[13px] text-fg-muted">{t("Noch kein Webhook.")}</li>}
        {q.data?.map((w) => <WebhookRow key={w.id} w={w} />)}
      </ul>
      <DialogShell open={open} onClose={close} title={secret ? t("Webhook angelegt") : t("Webhook hinzufügen")} description={secret ? undefined : t("Nur https-Adressen, die aus dem Internet erreichbar sind.")}>
        {secret ? (
          <SecretOnce label={t("Signatur-Geheimnis")} value={secret} hint={t("Damit prüft euer System, dass eine Meldung wirklich von Logiful kommt (Header X-Logiful-Signature). Nur jetzt sichtbar.")} onDone={close} />
        ) : (
          <>
            <label className="block mt-4"><span className="text-[12px] font-medium text-fg-muted">{t("Adresse")}</span><Input autoFocus value={url} onChange={(e) => setUrl(e.target.value)} placeholder={t("https://erp.example.de/logiful")} className="mt-1 font-mono" /></label>
            <label className="block mt-3"><span className="text-[12px] font-medium text-fg-muted">{t("Beschreibung (optional)")}</span><Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t("z. B. ERP Produktion")} className="mt-1" /></label>
            <fieldset className="mt-4">
              <legend className="text-[12px] font-medium text-fg-muted">{t("Ereignisse")}</legend>
              <div className="mt-1.5 grid gap-1.5">
                {WEBHOOK_EVENT_KEYS.map((e) => (
                  <label key={e} className="flex items-center gap-2 text-[13px] cursor-pointer">
                    <input type="checkbox" checked={events.includes(e)} onChange={(x) => setEvents((s) => (x.target.checked ? [...s, e] : s.filter((y) => y !== e)))} className="accent-ink" />
                    {WEBHOOK_EVENTS[e]} <span className="font-mono text-[11.5px] text-fg-subtle">{e}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="ghost" onClick={close}>{t("Abbrechen")}</Button>
              <Button variant="primary" loading={create.isPending} disabled={!/^https:\/\/.+/.test(url.trim()) || !events.length} onClick={() => create.mutate()}>{t("Anlegen")}</Button>
            </div>
          </>
        )}
      </DialogShell>
    </Card>
  );
}

function DocsCard() {
  const spec = useQuery({ queryKey: ["openapi"], queryFn: api.integrations.openapi, staleTime: 60 * 60_000 });
  const base = typeof window !== "undefined" ? window.location.origin : "";
  const [tab, setTab] = useState<"curl" | "node" | "php">("curl");
  const samples = {
    curl: `curl ${base}/v1/orders?status=shipped \\\n  -H "Authorization: Bearer lc_…"`,
    node: `// Webhook-Signatur prüfen (Node.js)\nimport { createHmac, timingSafeEqual } from "node:crypto";\n\nfunction verify(rawBody, header, secret) {\n  const [t, v1] = header.split(",").map((p) => p.split("=")[1]);\n  if (Math.abs(Date.now() / 1000 - Number(t)) > 300) return false; // max. 5 Min. alt\n  const expected = createHmac("sha256", secret).update(\`\${t}.\${rawBody}\`).digest("hex");\n  return timingSafeEqual(Buffer.from(expected), Buffer.from(v1));\n}`,
    php: `<?php // Webhook-Signatur prüfen (PHP)\n[$t, $v1] = array_map(fn($p) => explode('=', $p)[1], explode(',', $_SERVER['HTTP_X_LOGIFUL_SIGNATURE']));\n$body = file_get_contents('php://input');\n$ok = abs(time() - (int)$t) <= 300\n  && hash_equals(hash_hmac('sha256', "$t.$body", $secret), $v1);`,
  };
  const endpoints = Object.entries(spec.data?.paths ?? {}).flatMap(([path, ops]) => Object.entries(ops).map(([method, op]) => ({ method: method.toUpperCase(), path, summary: op.summary ?? "", tag: op.tags?.[0] ?? "" })));
  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-start gap-3">
        <span className="size-9 rounded-xl grid place-items-center bg-surface-2 text-fg-muted"><BookOpen className="size-4" /></span>
        <div className="min-w-0 flex-1">
          <h2 className="font-semibold">{t("Dokumentation")}</h2>
          <p className="text-[12.5px] text-fg-muted mt-0.5">{t("REST-API v1 unter")} <code className="font-mono">{base}/v1</code>{t(". Listen sind fortlaufend über")} <code className="font-mono">{t("next_cursor")}</code> {t("abholbar. Grenze: 600 Anfragen je Minute und Schlüssel.")}</p>
        </div>
        <a href="/v1/openapi.json" target="_blank" rel="noreferrer"><Button size="sm">{t("OpenAPI (JSON)")}</Button></a>
      </div>
      <div className="mt-4 rounded-xl border border-border overflow-hidden">
        <table className="w-full text-[12.5px]">
          <tbody>
            {endpoints.map((e) => (
              <tr key={`${e.method} ${e.path}`} className="border-b border-border last:border-0">
                <td className="pl-3 pr-2 py-1.5 w-16"><span className={cx("font-mono text-[11px] font-semibold", e.method === "GET" ? "text-info" : "text-success")}>{e.method}</span></td>
                <td className="px-2 py-1.5 font-mono">{e.path}</td>
                <td className="px-2 py-1.5 text-fg-muted">{t(e.summary)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4">
        <div className="inline-flex p-0.5 rounded-lg bg-surface-2 border border-border">
          {(["curl", "node", "php"] as const).map((k) => (
            <button key={k} onClick={() => setTab(k)} className={cx("h-7 px-3 rounded-md text-[12.5px] font-medium", tab === k ? "bg-surface shadow-sm" : "text-fg-muted hover:text-fg")}>{k === "curl" ? t("Abruf (curl)") : k === "node" ? t("Signatur prüfen (Node.js)") : t("Signatur prüfen (PHP)")}</button>
          ))}
        </div>
        <div className="relative mt-2">
          <pre className="rounded-xl bg-ink text-ink-fg p-4 text-[12px] leading-relaxed overflow-x-auto"><code>{samples[tab]}</code></pre>
          <button onClick={() => copy(samples[tab])} className="absolute top-2 right-2 size-7 grid place-items-center rounded-md text-ink-fg/70 hover:text-ink-fg hover:bg-white/10" aria-label={t("Kopieren")}><Copy className="size-3.5" /></button>
        </div>
      </div>
    </Card>
  );
}

/** Schnittstelle: API-Schlüssel, Webhooks, Dokumentation (Administratoren) */
export function IntegrationsPage() {
  const { role } = useTenant();
  if (!canManage(role))
    return (
      <div className="pb-16">
        <PageHeader title={t("Schnittstelle")} />
        <EmptyState icon={<KeyRound className="size-5" />} title={t("Nur für Administratoren")}>{t("API-Schlüssel und Webhooks verwalten Administratoren eures Teams.")}</EmptyState>
      </div>
    );
  return (
    <div className="pb-16">
      <PageHeader title={t("Schnittstelle")} description={t("Logiful mit eurer Warenwirtschaft, eurem ERP oder eigenen Tools verbinden.")} />
      <div className="px-4 md:px-8 space-y-4 max-w-4xl">
        <ApiKeysCard />
        <WebhooksCard />
        <DocsCard />
      </div>
    </div>
  );
}
